import { useState, useRef, useEffect, useCallback } from "react";
import { MousePointer2, Pencil, Check } from "lucide-react";

/**
 * Animated cursor tutorial. Two phases:
 *  1) Connect: glides to two rows, taps with ripples, draws a demo thread.
 *  2) Rename (optional): glides to the pencil, opens a mock rename card, types
 *     a sample name char-by-char, taps the check button, and shows a final tip.
 *
 * Nothing is written to the real DB — the rename overlay is a floating ghost card
 * rendered by this component so it can be safely replayed forever.
 */
export default function CursorGuide({
  containerRef, fromId, toId, loop, messages, storageKey, playSignal = 0,
  renamePencilId, renameRowId, renameSampleName, renameMessages,
}) {
  const [st, setSt] = useState(null);
  const timers = useRef([]);

  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const at = (ms, fn) => timers.current.push(setTimeout(fn, ms));

  const getPos = useCallback((id) => {
    const c = containerRef.current;
    if (!c) return null;
    const el = c.querySelector(`[data-conn-id="${id}"]`);
    if (!el) return null;
    const cr = c.getBoundingClientRect(), r = el.getBoundingClientRect();
    return {
      x: r.left + Math.min(r.width * 0.35, 120) - cr.left, y: r.top + r.height / 2 - cr.top,
      rx: r.right - cr.left, lx: r.left - cr.left, my: r.top + r.height / 2 - cr.top,
      cx: r.left + r.width / 2 - cr.left, cy: r.top + r.height / 2 - cr.top,
      w: r.width, h: r.height, top: r.top - cr.top, left: r.left - cr.left,
    };
  }, [containerRef]);

  const play = useCallback(() => {
    const p1 = getPos(fromId), p2 = getPos(toId);
    if (!p1 || !p2) return;
    clear();
    let d;
    if (loop) {
      const off = 56 + Math.abs(p2.my - p1.my) * 0.18;
      d = `M ${p1.rx},${p1.my} C ${p1.rx + off},${p1.my} ${p2.rx + off},${p2.my} ${p2.rx},${p2.my}`;
    } else {
      const dx = Math.max((p2.lx - p1.rx) / 2, 40);
      d = `M ${p1.rx},${p1.my} C ${p1.rx + dx},${p1.my} ${p2.lx - dx},${p2.my} ${p2.lx},${p2.my}`;
    }
    const upd = (patch) => setSt((s) => (s ? { ...s, ...patch } : s));
    setSt({ x: p1.x + 90, y: p1.y + 100, msg: "", d, thread: "none", ripple: null, fading: false, rename: null });
    // ── PHASE 1: connect two units ───────────────────────────────────────
    at(200, () => upd({ x: p1.x, y: p1.y, msg: messages[0] }));
    at(1200, () => upd({ ripple: { x: p1.x, y: p1.y, k: 1 } }));
    at(1800, () => upd({ x: p2.x, y: p2.y, msg: messages[1] }));
    at(2900, () => upd({ ripple: { x: p2.x, y: p2.y, k: 2 } }));
    at(3100, () => upd({ thread: "draw" }));
    at(4000, () => upd({ thread: "solid", msg: messages[2] }));

    const hasRename = renamePencilId && renameRowId && renameSampleName && renameMessages;
    if (!hasRename) {
      at(6800, () => upd({ fading: true }));
      at(7400, () => { setSt(null); if (storageKey) localStorage.setItem(storageKey, "1"); });
      return;
    }
    // ── PHASE 2: rename demo ─────────────────────────────────────────────
    const pencil = getPos(renamePencilId), row = getPos(renameRowId);
    if (!pencil || !row) {
      at(6800, () => upd({ fading: true }));
      at(7400, () => { setSt(null); if (storageKey) localStorage.setItem(storageKey, "1"); });
      return;
    }
    // Mock rename-card geometry: covers just the unit-name area of the row
    const card = { left: row.left + 12, top: row.top + 6, width: Math.min(280, row.w - 24), height: row.h - 12 };
    const inputCx = card.left + 100;
    const inputCy = card.top + card.height / 2;
    const checkCx = card.left + card.width - 22;
    const checkCy = card.top + card.height / 2;

    at(6800, () => upd({ x: pencil.cx, y: pencil.cy, msg: renameMessages[0] }));
    at(7800, () => upd({ ripple: { x: pencil.cx, y: pencil.cy, k: 3 } }));
    at(8200, () => upd({ rename: { ...card, typed: "", showCheck: false } }));
    at(8400, () => upd({ x: inputCx, y: inputCy, msg: renameMessages[1] }));
    // Type the sample name char-by-char
    const chars = renameSampleName;
    chars.split("").forEach((ch, i) => {
      at(8800 + i * 90, () => setSt((s) => s && s.rename
        ? { ...s, rename: { ...s.rename, typed: s.rename.typed + ch } }
        : s));
    });
    const typedEnd = 8800 + chars.length * 90 + 250;
    at(typedEnd, () => upd({ x: checkCx, y: checkCy }));
    at(typedEnd + 500, () => upd({ ripple: { x: checkCx, y: checkCy, k: 4 } }));
    at(typedEnd + 700, () => upd({ rename: null, msg: renameMessages[2] }));
    at(typedEnd + 3200, () => upd({ fading: true }));
    at(typedEnd + 3800, () => { setSt(null); if (storageKey) localStorage.setItem(storageKey, "1"); });
  }, [fromId, toId, loop, messages, storageKey, getPos, renamePencilId, renameRowId, renameSampleName, renameMessages]);

  useEffect(() => {
    if (storageKey && !localStorage.getItem(storageKey)) {
      const t = setTimeout(play, 900);
      return () => { clearTimeout(t); clear(); };
    }
    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (playSignal > 0) play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playSignal]);

  if (!st) return null;
  return (
    <div data-testid="cursor-guide-overlay" className={`absolute inset-0 z-20 pointer-events-none transition-opacity duration-500 ${st.fading ? "opacity-0" : "opacity-100"}`}>
      <div className="absolute inset-0 bg-slate-900/10 rounded-2xl" />
      <svg className="absolute inset-0 w-full h-full" style={{ overflow: "visible" }}>
        {st.thread !== "none" && (
          <path d={st.d} fill="none" stroke="#4F46E5" strokeWidth="3" strokeLinecap="round"
            pathLength="1" strokeDasharray="1" strokeDashoffset={st.thread === "draw" ? undefined : 0}
            className={st.thread === "draw" ? "guide-draw" : ""} />
        )}
      </svg>
      {/* Mock rename card — appears over the first unit row during Phase 2 */}
      {st.rename && (
        <div
          data-testid="cursor-guide-rename-card"
          className="absolute bg-white rounded-xl border-2 border-[#4F46E5] shadow-lg flex items-center gap-2 px-3"
          style={{ left: st.rename.left, top: st.rename.top, width: st.rename.width, height: st.rename.height, animation: "bubble-in 0.2s ease-out" }}
        >
          <Pencil size={12} className="text-[#4F46E5] shrink-0" />
          <span className="text-sm text-slate-800 font-medium flex-1 truncate">
            {st.rename.typed}
            <span className="inline-block w-[1.5px] h-4 bg-[#4F46E5] ml-0.5 align-middle" style={{ animation: "cursor-blink 0.9s steps(2,end) infinite" }} />
          </span>
          <span className="w-6 h-6 rounded-full bg-[#4F46E5] text-white flex items-center justify-center shrink-0">
            <Check size={12} />
          </span>
        </div>
      )}
      {st.ripple && <span key={st.ripple.k} className="guide-ripple" style={{ left: st.ripple.x, top: st.ripple.y }} />}
      <div className="guide-cursor" style={{ left: st.x, top: st.y }}>
        <MousePointer2 size={28} className="text-slate-900" fill="#ffffff" strokeWidth={1.5} style={{ filter: "drop-shadow(0 3px 6px rgba(15,23,42,0.35))" }} />
        {st.msg && <span className="guide-bubble">{st.msg}</span>}
      </div>
    </div>
  );
}
